/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useRef } from 'react';

import createLoadableComponent from 'js/lib/createLoadableComponent';
import { isAuthenticatedUser } from 'js/lib/user';

import CampaignPromoBarModuleRenderer, {
  useShouldShowCampaignPromoBarModule,
} from 'bundles/campaigns/module-renderers/CampaignPromoBarModuleRenderer';
import { newNumberOfProductsCourseraPlusNoPlusSign } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import PromoWarningModal from 'bundles/coursera-plus-landing-page/components/PromoWarningModal';
import type { RichTextObject } from 'bundles/coursera-plus-landing-page/components/SimpleRichTextContentful';
import CourseraPlusHeroModule from 'bundles/coursera-plus-landing-page/components/redesign/CourseraPlusHeroModule';
import CourseraPlusPartnerLogos from 'bundles/coursera-plus-landing-page/components/redesign/CourseraPlusPartnerLogos';
import CourseraPlusValueProps from 'bundles/coursera-plus-landing-page/components/redesign/CourseraPlusValueProps';
import FixedPageNavigation from 'bundles/coursera-plus-landing-page/components/redesign/FixedPageNavigation';
import LearnerStatisticsModule from 'bundles/coursera-plus-landing-page/components/redesign/LearnerStatisticsModule';
import {
  HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE,
  landingPageTypeChoices,
} from 'bundles/coursera-plus-landing-page/contentful/constants';
import { useLandingPageExtended } from 'bundles/coursera-plus-landing-page/contentfulData/useLandingPageData';
import usePromoWarningModalMessage from 'bundles/coursera-plus-landing-page/hooks/usePromoWarningModalMessage';
import {
  getCollectionLandingPageFAQs,
  getCourseraPlusPaidMarketingPageFAQs,
} from 'bundles/coursera-plus-landing-page/utils/getCourseraPlusFAQs';
import { useProductVariantUrlAction } from 'bundles/coursera-plus-landing-page/utils/useProductVariantUrlAction';
import { ENROLL } from 'bundles/enroll/utils/enrollActionParams';
import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';
import courseraPlusExperiments from 'bundles/epic/clients/courseraPlus';
import { RegionalPromotionBanner } from 'bundles/front-page/components/banners/regional-promotion-banner/RegionalPromotionBanner';

import _t from 'i18n!nls/coursera-plus-landing-page';

const styles = {
  // Note: This combination of styling fixes an accessibility issue where the PriceComparison component
  // requires horizontal scrolling, which causes the entire page to have horizontal scrolling despite
  // it just being extra whitespace for all other sections.
  // This fixes it so that only the PriceComparison component has horizontal scrolling: https://coursera.atlassian.net/browse/ACCESS-2635.
  // The PriceComparison block having horizontal scrolling should be fine for accessibility according to this StackOverflow post: https://stackoverflow.com/questions/61940426/is-horizontal-scrolling-within-a-container-considered-inaccessible-even-though/61942943#61942943
  wrapper: css`
    max-width: 100%;
    overflow-x: hidden;
    position: relative;
  `,
};

// Lazy load all components that show up below the fold
const CourseraPlusProductCollection = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/redesign/CourseraPlusProductCollection')
);

const CourseraPlusSearchContainer = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/CourseraPlusSearchContainer')
);

const CourseraPlusTestimonials = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/redesign/CourseraPlusTestimonials')
);

const PriceComparison = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/priceComparison/PriceComparisonContentful')
);

const FAQsModule = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/LazyFAQsModule')
);

const CourseraPlusCitations = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/redesign/CourseraPlusCitations')
);

const CourseraPlusFooter = createLoadableComponent(
  () => import('bundles/coursera-plus-landing-page/components/redesign/CourseraPlusFooter')
);

export const sections = {
  SEARCH: 'search',
  FAQ: 'faq',
  UNIVERSITIES: 'universities',
  RECOMMENDATIONS: 'recommendations',
  STORIES: 'stories',
  PROPOSITIONS: 'propositions',
  TOP: 'top',
};

export const LandingPageContentfulContent = () => {
  const extendedLandingPage = useLandingPageExtended();
  const heroRef = useRef<HTMLDivElement>(null);
  const { action, variant } = useProductVariantUrlAction();
  const shouldShowCampaignPromoBar = useShouldShowCampaignPromoBarModule();

  const {
    isCourseraPlusOwner,
    userHasCourseraPlusFreeTrial,
    landingPageType,
    customProductCollectionAtTheTop: customProductCollection,
  } = extendedLandingPage;

  const {
    cutValueProps,
    cutCourseSearch,
    cutPriceComparison,
    cutFaq,
    cutPartnerLogos,
    cutFooter,
    cutLearnerOutcome,
    cutRecommendations,
    explanationForFaq,
    modalForUserWhoOwnsCourseraPlus,
    modalForUserWhoUsedUpTheirFreeTrial,
    skipQuestionInFaqAboutFreeTrial,
    slug,
  } = extendedLandingPage.landingPage;

  const isComputerScienceCollectionPage = landingPageType === landingPageTypeChoices.COMPUTER_SCIENCE_COLLECTION;
  const isQ32023TentpolePromotion = ['onedollar-aug-2023', 'annual-aug-2023'].includes(slug as string);
  const collectionTitleOverride: string | undefined =
    slug === 'onedollar-nov-2023' ? _t('Launch your career') : undefined;

  useEffect(() => {
    // If we have a variable named variant in the URL query,
    // we want to avoid recording an impression for the experiment.
    // This is because for some variants, the design at log out is different
    // from the design after log in, and we want to avoid such learners as they
    // might not represent the success of the new designs.
    if (
      !variant &&
      action !== ENROLL &&
      !isCourseraPlusOwner &&
      slug === HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE &&
      isAuthenticatedUser()
    ) {
      // Record impression for Coursera Plus Annual with 7 days Free trial
      // https://tools.coursera.org/epic/experiment/qC4vUeJzEe-6NxLsF4c5gw

      courseraPlusExperiments.get('courseraPlusAnnualWith7dayFreeTrialTest');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, variant]);

  useEffect(() => {
    if (!isCourseraPlusOwner && slug === HIDDEN_SLUG_FOR_DEFAULT_LANDING_PAGE && isAuthenticatedUser()) {
      // Recording impression for India price test.
      // Epic: https://tools.coursera.org/epic/experiment/WS16jhbwEfCHlw4giApofQ
      GoogleCertificateEpicClient.get('india-price-decrease');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCourseraPlusOwner, slug]);

  const { modalMessage, redirectUrlIfNotEligible } = usePromoWarningModalMessage({
    modalForUserWhoOwnsCourseraPlus,
    modalForUserWhoUsedUpTheirFreeTrial,
    isCourseraPlusOwner,
    userHasCourseraPlusFreeTrial,
  });

  let faqs = getCourseraPlusPaidMarketingPageFAQs(explanationForFaq as RichTextObject, skipQuestionInFaqAboutFreeTrial);
  if (isComputerScienceCollectionPage) {
    faqs = getCollectionLandingPageFAQs(explanationForFaq as RichTextObject, skipQuestionInFaqAboutFreeTrial);
  }

  const getPromotionalBanner = () => {
    if (shouldShowCampaignPromoBar) {
      return <CampaignPromoBarModuleRenderer />;
    }

    if (extendedLandingPage.hideRegionalPromotionBanner) {
      return null;
    }

    return <RegionalPromotionBanner variant="blue" />;
  };

  return (
    <div css={styles.wrapper}>
      <FixedPageNavigation heroRef={heroRef} />
      {getPromotionalBanner()}
      {modalMessage && <PromoWarningModal message={modalMessage} redirectUrlIfNotEligible={redirectUrlIfNotEligible} />}
      <div ref={heroRef}>
        <CourseraPlusHeroModule />
      </div>
      {!cutPartnerLogos && <CourseraPlusPartnerLogos />}
      {!cutValueProps && <CourseraPlusValueProps />}
      <LearnerStatisticsModule />
      {!cutRecommendations && (
        <CourseraPlusProductCollection
          collectionSlug={customProductCollection}
          titleOverride={collectionTitleOverride}
        />
      )}
      {!cutCourseSearch && (
        <CourseraPlusSearchContainer
          additionalSearchResultsProps={{ removeLinks: true }}
          ownsCourseraPlus={isCourseraPlusOwner}
          numberOfProductsInPlusOverride={
            isQ32023TentpolePromotion ? newNumberOfProductsCourseraPlusNoPlusSign : undefined
          }
        />
      )}
      {!cutLearnerOutcome && <CourseraPlusTestimonials />}
      {!cutPriceComparison && <PriceComparison landingPage={extendedLandingPage} />}
      {!cutFaq && <FAQsModule faqs={faqs} />}
      <CourseraPlusCitations />
      {!cutFooter && <CourseraPlusFooter />}
    </div>
  );
};

export default LandingPageContentfulContent;
